import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import {
  AlertTriangle,
  ArrowLeft,
  Camera,
  CheckCircle2,
  Download,
  Loader2,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import React, { Suspense, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import * as api from "@/api/returns";
import { REASON_BUTTONS, reasonLabel } from "@/lib/returns";
import { getOrderCheckConfig } from "@/api/order_check";
import { Button } from "@/components/ui/button";
import { isValidEan13 } from "@/lib/ean13";
import { fmtDate } from "@/lib/format";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { useScanCapture } from "@/lib/use-scan-capture";
import { parseScan } from "@/lib/order-scan";
import { cn } from "@/lib/utils";
import { type BlockedScan, BlockedScanBanner } from "@/pages/fbs/ScanBanner";

// FastAPI detail: строка либо dict {code,message} (structured-сигнал partia_not_found).
type BackendError = {
  response?: { data?: { detail?: string | { code?: string; message?: string } } };
  message?: string;
};
function parseBackendError(e: BackendError): { code: string; message: string } {
  const detail = e.response?.data?.detail;
  if (typeof detail === "string") return { code: "", message: detail };
  if (detail && typeof detail === "object") {
    return {
      code: typeof detail.code === "string" ? detail.code : "",
      message: typeof detail.message === "string" ? detail.message : e.message ?? "",
    };
  }
  return { code: "", message: e.message ?? "" };
}

interface CardState {
  raw: string;
  lookup: api.LookupResponse;
  partiaNameInput: string;
  acceptWithoutPartia: boolean;
}
type Draft =
  | { phase: "idle" }
  | { phase: "lookup"; raw: string }
  | ({ phase: "card" } & CardState)
  | { phase: "error"; message: string };

function makeCard(raw: string, lookup: api.LookupResponse): Extract<Draft, { phase: "card" }> {
  return { phase: "card", raw, lookup, partiaNameInput: lookup.partia_name ?? "", acceptWithoutPartia: false };
}
// ─────────────────────────── entry ────────────────────────────────────────
export function IntakeSessionPageV2(): React.ReactElement {
  // Чанк камеры тянем при ВХОДЕ: устаревший чанк после выката
  // перезагружает страницу, и делать это в момент тапа «Сканировать»
  // значит прерывать работу ровно посередине.
  usePreloadChunk(CameraScanner);
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const detailQ = useQuery({ queryKey: ["returns", "session", id], queryFn: () => api.getSession(id), enabled: !!id });
  const configQ = useQuery({ queryKey: ["oc-config"], queryFn: getOrderCheckConfig, staleTime: 300000 });
  const scanMethod = configQ.data?.scan_method ?? "tsd";
  const refetch = () => qc.invalidateQueries({ queryKey: ["returns", "session", id] });

  const [draft, setDraft] = useState<Draft>({ phase: "idle" });
  const [partiaNotFound, setPartiaNotFound] = useState<{ payload: api.ItemCreate; partiaName: string } | null>(null);
  const [cameraScan, setCameraScan] = useState(false);
  const [feedOpen, setFeedOpen] = useState(false);
  const [dialog, setDialog] = useState<null | "complete" | "reopen">(null);
  const [blocked, setBlocked] = useState<BlockedScan | null>(null);
  const partiaInputRef = useRef<HTMLInputElement>(null);
  const resolvingRef = useRef(false);

  const detail = detailQ.data;
  const isOpen = detail?.status === "open";

  const lookupMut = useMutation({ mutationFn: (body: api.LookupRequest) => api.lookupScan(id, body) });
  const addMut = useMutation({
    mutationFn: (body: api.ItemCreate) => api.addItem(id, body),
    onSuccess: () => {
      refetch();
      setDraft({ phase: "idle" });
      setPartiaNotFound(null);
      navigator.vibrate?.(40);
      toast.success("Позиция добавлена");
    },
  });
  const delMut = useMutation({
    mutationFn: (itemId: string) => api.deleteItem(id, itemId),
    onSuccess: () => refetch(),
    onError: (e) => toast.error(parseBackendError(e as BackendError).message || "Ошибка удаления"),
  });
  const completeMut = useMutation({
    mutationFn: () => api.completeSession(id),
    onSuccess: () => {
      setDialog(null);
      refetch();
      qc.invalidateQueries({ queryKey: ["returns", "sessions"] });
    },
    onError: (e) => {
      setDialog(null);
      toast.error(parseBackendError(e as BackendError).message || "Не удалось завершить");
    },
  });
  const reopenMut = useMutation({
    mutationFn: () => api.reopenSession(id),
    onSuccess: () => {
      setDialog(null);
      refetch();
      qc.invalidateQueries({ queryKey: ["returns", "sessions"] });
      toast.success("Приёмка возвращена в работу");
    },
    onError: (e) => {
      setDialog(null);
      toast.error(parseBackendError(e as BackendError).message || "Не удалось вернуть в работу");
    },
  });

  // ── обработка скана ────────────────────────────────────────────────────
  function startLookup(raw: string) {
    // Разбор — общий с остальными скан-экранами (`parseScan`). Свой путь через
    // `dm-parser` тут был вторым источником правды: расширение под ГИСМ
    // (91/92 и короткая форма) пришлось бы вносить дважды.
    const scan = parseScan(raw);
    // Контрольная сумма EAN13 — отдельный сигнал: он означает, что сканер
    // недочитал код, а не что товара нет в 1С. `parseScan` пропускает
    // 13-значные коды и с битой суммой (общий фолбэк «цифры 8..14»).
    if (scan.kind === "ean13" && !isValidEan13(raw)) {
      setDraft({ phase: "error", message: "Невалидный EAN13" });
      return;
    }
    const request =
      scan.kind === "ean13" && scan.ean13
        ? { scan_kind: "ean13" as const, raw_scan: raw, ean13: scan.ean13 }
        : scan.kind === "datamatrix" && scan.gtin
          ? {
              scan_kind: "datamatrix" as const,
              raw_scan: raw,
              gtin: scan.gtin,
              serial: scan.serial,
            }
          : null;
    if (!request) {
      setDraft({
        phase: "error",
        message: "Неизвестный код. Отсканируйте EAN13 или DataMatrix.",
      });
      return;
    }
    setDraft({ phase: "lookup", raw });
    lookupMut.mutate(request, {
      onSuccess: (lookup) => setDraft(makeCard(raw, lookup)),
      onError: (e: BackendError) =>
        setDraft({
          phase: "error",
          message: parseBackendError(e).message || "Ошибка поиска",
        }),
    });
  }

  function handleScan(raw: string) {
    if (!isOpen || draft.phase !== "idle" || feedOpen || dialog || partiaNotFound) return;
    if (resolvingRef.current) {
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    resolvingRef.current = true;
    setBlocked(null);
    try {
      startLookup(raw.trim());
    } finally {
      resolvingRef.current = false;
    }
  }

  function onPickReason(rt: api.ReasonType, sub: api.ReasonSubtype | null) {
    if (draft.phase !== "card") return;
    const l = draft.lookup;
    if (!l.nomenclature_ref) {
      toast.error("Номенклатура не определена — нельзя сохранить");
      return;
    }
    const inputName = draft.partiaNameInput.trim();
    const lookupName = (l.partia_name ?? "").trim();
    const base: api.ItemCreate = {
      scan_kind: l.scan_kind,
      raw_scan: draft.raw,
      gtin: l.gtin,
      serial: l.serial,
      ean13: l.ean13,
      nomenclature_ref: l.nomenclature_ref,
      nomenclature_name: l.nomenclature_name,
      nomenclature_article: l.nomenclature_article,
      characteristic_ref: l.characteristic_ref,
      characteristic_name: l.characteristic_name,
      reason_type: rt,
      reason_subtype: sub,
    };
    if (draft.acceptWithoutPartia) {
      if (!inputName) {
        toast.error("Введите номер партии");
        return;
      }
      addMut.mutate(
        { ...base, partia_ref: null, partia_name: inputName, partia_source: "none", accept_without_partia: true },
        { onError: (e: BackendError) => toast.error(parseBackendError(e).message || "Ошибка сохранения") },
      );
      return;
    }
    const usedLookupRef = !!l.partia_ref && inputName === lookupName;
    const partia_ref = usedLookupRef ? l.partia_ref : null;
    const partia_name = usedLookupRef ? l.partia_name : inputName;
    if (!partia_ref && !partia_name) {
      toast.error("Введите номер партии");
      return;
    }
    const payload: api.ItemCreate = { ...base, partia_ref, partia_name, partia_source: usedLookupRef ? "hs" : "manual" };
    addMut.mutate(payload, {
      onError: (e: BackendError) => {
        const parsed = parseBackendError(e);
        if (parsed.code === "partia_not_found") {
          setPartiaNotFound({ payload, partiaName: partia_name ?? "" });
          return;
        }
        toast.error(parsed.message || "Ошибка сохранения");
      },
    });
  }

  const scanEnabled = !!isOpen && draft.phase === "idle" && !cameraScan && !feedOpen && !dialog && !partiaNotFound;
  // Код пришёл, пока перехват выключен: не теряем молча — называем, почему
  // он не принят и что сделать. Завершённую приёмку сканировать нечем.
  function onBlocked(raw: string) {
    if (!isOpen) return;
    const reason =
      draft.phase === "lookup"
        ? "Предыдущий скан ещё ищется — дождитесь ответа и отсканируйте ещё раз."
        : draft.phase === "card"
          ? "Сначала выберите причину возврата для позиции на экране."
          : draft.phase === "error"
            ? "Сначала нажмите «Продолжить скан»."
            : cameraScan
              ? "Открыта камера — закройте её, чтобы сканировать сканером."
              : "Сначала закройте открытое окно.";
    setBlocked({ raw, reason });
  }
  const scan = useScanCapture({ enabled: scanEnabled, onScan: handleScan, onBlocked });

  if (detailQ.isLoading || !detail) {
    return (
      <div className="mx-auto flex h-app max-w-md items-center justify-center bg-slate-50 text-slate-400">
        <Loader2 className="size-7 animate-spin" />
      </div>
    );
  }

  const mp = api.marketplaceTitle(detail.marketplace);
  const utilityCount = detail.items.filter((i) => i.reason_type === "utility").length;
  const busy = draft.phase === "lookup";
  const chip: "ready" | "busy" | "paused" = busy ? "busy" : scanEnabled ? "ready" : "paused";

  const onDownloadMemo = async () => {
    try {
      await api.downloadMemo(detail.id);
    } catch (e) {
      toast.error(parseBackendError(e as BackendError).message || "Ошибка скачивания");
    }
  };

  // ── completed ──────────────────────────────────────────────────────────
  if (!isOpen) {
    return (
      <div className="mx-auto flex h-app max-w-md flex-col bg-slate-50">
        <AppBar mp={mp} number={detail.display_number} count={detail.items.length} chip="paused" onBack={() => navigate("/returns")} />
        <div className="flex-1 space-y-3 overflow-y-auto p-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <div className="flex items-center gap-2 text-[16px] font-bold text-emerald-700">
              <CheckCircle2 className="size-5" /> {detail.status === "cancelled" ? "Приёмка отменена" : "Приёмка завершена"}
            </div>
            <div className="mt-1 text-[13px] text-emerald-900">Позиций принято: <b>{detail.items.length}</b>{utilityCount > 0 ? ` · на утиль: ${utilityCount}` : ""}</div>
          </div>
          <button type="button" onClick={() => setFeedOpen(true)} className="flex w-full items-center justify-between rounded-lg border bg-white p-3 text-left">
            <span className="text-[13px] font-semibold">Позиции ({detail.items.length})</span>
            <ArrowLeft className="size-4 rotate-180 text-slate-300" />
          </button>
          {utilityCount > 0 && (
            <Button variant="outline" className="h-12 w-full" onClick={onDownloadMemo}>
              <Download className="size-4" /> Служебная записка на утиль ({utilityCount})
            </Button>
          )}
          {detail.status === "completed" && (
            <Button variant="outline" className="h-12 w-full" onClick={() => setDialog("reopen")}>
              <RotateCcw className="size-4" /> Вернуть в работу
            </Button>
          )}
        </div>
        {feedOpen && <ItemsSheet detail={detail} canDelete={false} onClose={() => setFeedOpen(false)} onDelete={() => {}} />}
        {dialog === "reopen" && (
          <Dialog
            title="Вернуть в работу?"
            body="Приёмку можно будет снова редактировать (добавлять/удалять позиции)."
            confirmLabel="Вернуть"
            confirmBusy={reopenMut.isPending}
            onConfirm={() => reopenMut.mutate()}
            onCancel={() => setDialog(null)}
          />
        )}
      </div>
    );
  }

  // ── open ───────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto flex h-app max-w-md flex-col bg-slate-50">
      <input ref={scan.scanInputRef} {...scan.scanInputProps} className="pointer-events-none fixed left-0 top-0 size-px opacity-0" />

      <AppBar
        mp={mp}
        number={detail.display_number}
        count={detail.items.length}
        chip={chip}
        onBack={() => navigate("/returns")}
        menu={{
          showCamera: scanMethod === "camera" || scanMethod === "both",
          onCamera: () => setCameraScan(true),
          utilityCount,
          onMemo: onDownloadMemo,
        }}
      />

      {/* герой */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {blocked && (
          <div className="mb-2">
            <BlockedScanBanner blocked={blocked} onClose={() => setBlocked(null)} />
          </div>
        )}
        <Hero
          draft={draft}
          setDraft={setDraft}
          saving={addMut.isPending}
          partiaInputRef={partiaInputRef}
          onPickReason={onPickReason}
          onDismiss={() => setDraft({ phase: "idle" })}
        />
      </div>

      {/* лента */}
      <button type="button" onClick={() => setFeedOpen(true)} className="flex items-center justify-between border-t bg-white px-3 py-2 text-left">
        <span className="text-[13px] font-bold">Принято: {detail.items.length}</span>
        <span className="flex items-center gap-2 text-[11px] text-slate-500">
          {utilityCount > 0 ? `утиль ${utilityCount}` : "лента позиций"}
          <ArrowLeft className="size-4 rotate-180 text-slate-300" />
        </span>
      </button>

      {/* гейт */}
      <div className="border-t bg-white p-2">
        <button
          type="button"
          disabled={completeMut.isPending}
          onClick={() => setDialog("complete")}
          className={cn("flex h-14 w-full items-center justify-center gap-2 rounded-[10px] border text-[14px] font-bold", detail.items.length > 0 ? "bg-emerald-600 border-emerald-600 text-white" : "bg-slate-100 border-slate-200 text-slate-500")}
        >
          {completeMut.isPending && <Loader2 className="size-4 animate-spin" />}
          ✓ Завершить приёмку{detail.items.length > 0 ? ` (${detail.items.length})` : ""}
        </button>
      </div>

      {(scanMethod === "camera" || scanMethod === "both") && !cameraScan && (
        <button
          type="button"
          onClick={() => setCameraScan(true)}
          className="fixed bottom-24 right-4 z-30 flex h-14 items-center gap-2 rounded-full bg-sky-700 px-5 text-[14px] font-bold text-white shadow-lg"
        >
          <Camera className="size-5" /> Сканировать камерой
        </button>
      )}

      {cameraScan && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-40 bg-black">
            <CameraScanner
              onDetect={(raw) => {
                setCameraScan(false);
                handleScan(raw);
              }}
              onClose={() => setCameraScan(false)}
            />
          </div>
        </Suspense>
      )}

      {feedOpen && <ItemsSheet detail={detail} canDelete onClose={() => setFeedOpen(false)} onDelete={(itemId) => delMut.mutate(itemId)} />}

      {partiaNotFound && (
        <PartiaNotFoundModal
          partiaName={partiaNotFound.partiaName}
          saving={addMut.isPending}
          onAcceptWithoutPartia={() =>
            addMut.mutate(
              { ...partiaNotFound.payload, partia_ref: null, partia_source: "none", accept_without_partia: true },
              { onError: (e: BackendError) => toast.error(parseBackendError(e).message || "Ошибка сохранения") },
            )
          }
          onChangePartia={() => {
            setPartiaNotFound(null);
            setTimeout(() => partiaInputRef.current?.focus(), 0);
          }}
        />
      )}

      {dialog === "complete" && (
        <Dialog
          title="Завершить приёмку?"
          body={`Принято позиций: ${detail.items.length}. После завершения сессия закроется (можно будет вернуть в работу).`}
          confirmLabel="Завершить"
          confirmBusy={completeMut.isPending}
          onConfirm={() => completeMut.mutate()}
          onCancel={() => setDialog(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────── AppBar ───────────────────────────────────────
function AppBar({
  mp,
  number,
  count,
  chip,
  onBack,
  menu,
}: {
  mp: string;
  number: number | null;
  count: number;
  chip: "ready" | "busy" | "paused";
  onBack: () => void;
  menu?: { showCamera: boolean; onCamera: () => void; utilityCount: number; onMemo: () => void };
}): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const chipMap = {
    ready: { dot: "bg-emerald-400", bg: "bg-emerald-500/20", t: "Сканер готов" },
    busy: { dot: "bg-sky-400", bg: "bg-sky-500/20", t: "Ищем код…" },
    paused: { dot: "bg-slate-400", bg: "bg-white/10", t: "Скан на паузе" },
  }[chip];
  const item = "block w-full px-3 py-2.5 text-left text-[13px] hover:bg-slate-100";
  return (
    <div className="relative bg-slate-900 text-white">
      <div className="flex h-[46px] items-center gap-1 px-1">
        <button type="button" onClick={onBack} className="flex size-9 items-center justify-center" aria-label="Назад">
          <ArrowLeft className="size-5" />
        </button>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-[13px] font-bold">{number ? `#${number} · ` : ""}{mp}</div>
          <div className="truncate text-[10px] text-slate-400">Приёмка возвратов · принято {count}</div>
        </div>
        <button
          type="button"
          onClick={() => menu && setMenuOpen((s) => !s)}
          className={cn("mr-1 flex h-[30px] items-center gap-1.5 rounded-full px-2.5", chipMap.bg)}
        >
          <span className={cn("size-2 rounded-full", chipMap.dot)} />
          <span className="text-[11px] font-semibold">{chipMap.t}</span>
        </button>
      </div>
      {menuOpen && menu && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-2 top-[44px] z-40 w-56 overflow-hidden rounded-lg bg-white text-slate-800 shadow-xl">
            {menu.showCamera && (
              <button type="button" className={item} onClick={() => { setMenuOpen(false); menu.onCamera(); }}>Камера-сканер</button>
            )}
            {menu.utilityCount > 0 && (
              <button type="button" className={item} onClick={() => { setMenuOpen(false); menu.onMemo(); }}>Служебная записка на утиль ({menu.utilityCount})</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── Hero ─────────────────────────────────────────
function Hero({
  draft,
  setDraft,
  saving,
  partiaInputRef,
  onPickReason,
  onDismiss,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  saving: boolean;
  partiaInputRef: React.RefObject<HTMLInputElement>;
  onPickReason: (rt: api.ReasonType, sub: api.ReasonSubtype | null) => void;
  onDismiss: () => void;
}): React.ReactElement {
  if (draft.phase === "idle") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <div className="flex size-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-300">
          <svg viewBox="0 0 24 24" className="size-8" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 7V4h3M20 7V4h-3M4 17v3h3M20 17v3h-3M3 12h18" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="text-[17px] font-bold text-slate-700">Сканируйте возврат</div>
        <div className="text-[12px] text-slate-400">EAN13 или DataMatrix Честного знака</div>
      </div>
    );
  }
  if (draft.phase === "lookup") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 text-center">
        <Loader2 className="size-7 animate-spin text-sky-600" />
        <div className="text-[14px] font-bold text-sky-700">Ищем код…</div>
        <div className="font-mono text-[11px] text-slate-500">{draft.raw.slice(0, 40)}</div>
      </div>
    );
  }
  if (draft.phase === "error") {
    return (
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3">
        <div className="text-[18px] font-bold text-red-700">Ошибка скана</div>
        <div className="mt-1 text-[13px] text-red-900">{draft.message}</div>
        <Button variant="outline" className="mt-3 h-12 w-full" onClick={onDismiss}>
          Продолжить скан
        </Button>
      </div>
    );
  }
  // card
  const l = draft.lookup;
  const dmResolvedPartia = l.partia_ref !== null;
  return (
    <div className="space-y-3">
      <div className="rounded-xl border-2 border-slate-200 bg-white p-3">
        {l.nomenclature_article && <span className="font-mono text-[19px] font-bold">{l.nomenclature_article}</span>}
        <div className="mt-0.5 text-[15px] font-bold">
          {l.nomenclature_name ?? l.honest_sign_product_name ?? "Не найдено в 1С"}
        </div>
        {l.characteristic_name && <div className="text-[12px] text-slate-600">{l.characteristic_name}</div>}
        {dmResolvedPartia && (
          <div className="mt-1 text-[12px] text-emerald-700">
            Партия: <b>{l.partia_name}</b>{l.partia_expires_at ? ` · годен до ${fmtDate(l.partia_expires_at)}` : ""}
          </div>
        )}
        {l.honest_sign_batch_name && !dmResolvedPartia && <div className="mt-1 text-[12px] text-amber-700">ЧЗ партия: {l.honest_sign_batch_name}</div>}
        {l.not_found_reason && <div className="mt-1 text-[12px] text-amber-700">{l.not_found_reason}</div>}
        {l.honest_sign_warning && <div className="mt-1 text-[12px] text-amber-700">{l.honest_sign_warning}</div>}
      </div>

      {!dmResolvedPartia && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-2.5">
          <div className="mb-1 text-[12px] font-semibold text-amber-700">Номер партии:</div>
          <input
            ref={partiaInputRef}
            value={draft.partiaNameInput}
            onChange={(e) => setDraft({ ...draft, partiaNameInput: e.target.value })}
            placeholder="например 240101"
            className="h-12 w-full rounded-lg border px-3 text-[15px]"
          />
          <label className="mt-2 flex items-center gap-2 text-[13px]">
            <input type="checkbox" className="size-5 accent-amber-600" checked={draft.acceptWithoutPartia} disabled={saving} onChange={(e) => setDraft({ ...draft, acceptWithoutPartia: e.target.checked })} />
            Принять без партии (не сопоставлять с 1С)
          </label>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {REASON_BUTTONS.map((b) => (
          <button
            key={b.label}
            type="button"
            disabled={saving}
            onClick={() => onPickReason(b.reason_type, b.reason_subtype)}
            className={cn("h-16 rounded-xl text-[16px] font-bold transition-colors disabled:opacity-50", b.cls)}
          >
            {saving ? <Loader2 className="mx-auto size-5 animate-spin" /> : b.label}
          </button>
        ))}
      </div>

      <Button variant="ghost" className="h-11 w-full" onClick={onDismiss} disabled={saving}>
        <X className="size-4" /> Отмена
      </Button>
    </div>
  );
}

// ─────────────────────────── лента ────────────────────────────────────────
function ItemsSheet({
  detail,
  canDelete,
  onClose,
  onDelete,
}: {
  detail: api.SessionDetail;
  canDelete: boolean;
  onClose: () => void;
  onDelete: (itemId: string) => void;
}): React.ReactElement {
  const items = [...detail.items].reverse();
  return (
    <BottomSheet onClose={onClose} panelClassName="flex max-h-[90%] flex-col">
      <div className="flex items-center justify-between border-b p-3">
        <div className="text-[15px] font-bold">Принято ({items.length})</div>
        <button type="button" onClick={onClose} className="flex size-8 items-center justify-center"><X className="size-4" /></button>
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
        {items.length === 0 && <div className="py-6 text-center text-[13px] text-slate-400">Пока пусто</div>}
        {items.map((it) => (
          <div key={it.id} className="flex items-start justify-between gap-2 rounded-lg border bg-white p-2">
            <div className="min-w-0 text-[13px]">
              <div className="flex flex-wrap items-center gap-1">
                <span className={cn("rounded px-1 py-0.5 text-[10px] font-bold uppercase", it.scan_kind === "datamatrix" ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900")}>
                  {it.scan_kind === "datamatrix" ? "ЧЗ" : "ШК"}
                </span>
                {it.partia_source === "hs" && <span className="rounded bg-emerald-50 px-1 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Авто</span>}
                {it.partia_source === "manual" && <span className="rounded bg-blue-100 px-1 py-0.5 text-[10px] font-bold uppercase text-blue-900">Ручн</span>}
                {it.partia_source === "none" && <span className="rounded bg-red-100 px-1 py-0.5 text-[10px] font-bold uppercase text-red-900 ring-1 ring-red-300">Б/П</span>}
                <span className="truncate font-medium">{it.nomenclature_name ?? "(неизвестная номенклатура)"}</span>
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                {it.nomenclature_article && `Арт. ${it.nomenclature_article} · `}
                {it.partia_name ? `парт. ${it.partia_name} · ` : ""}
                {reasonLabel(it.reason_type, it.reason_subtype)}
              </div>
            </div>
            {canDelete && (
              <button type="button" onClick={() => onDelete(it.id)} className="flex size-8 shrink-0 items-center justify-center rounded-md border border-red-200 text-red-600" aria-label="Удалить">
                <Trash2 className="size-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────── модалка «партия не найдена» ───────────────────
function PartiaNotFoundModal({
  partiaName,
  saving,
  onAcceptWithoutPartia,
  onChangePartia,
}: {
  partiaName: string;
  saving: boolean;
  onAcceptWithoutPartia: () => void;
  onChangePartia: () => void;
}): React.ReactElement {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4" onClick={() => !saving && onChangePartia()}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="size-6 shrink-0 text-amber-600" />
          <div>
            <div className="text-[16px] font-bold">Партия не найдена в 1С</div>
            <div className="mt-1 text-[12px] text-slate-600">
              «{partiaName || "(пусто)"}» — такой партии нет в УНФ. Можно учесть позицию без привязки к партии, либо ввести другую.
            </div>
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <Button className="h-12 w-full bg-amber-600 text-white hover:bg-amber-700" onClick={onAcceptWithoutPartia} disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : "Учесть без партии"}
          </Button>
          <Button variant="outline" className="h-12 w-full" onClick={onChangePartia} disabled={saving}>
            Изменить партию
          </Button>
        </div>
      </div>
    </div>
  );
}
